/**
 * `/signup` (#371, D30): create an account with a founder-issued invite code.
 *
 * Sign-up is gated, not open. The founder hands each classmate a single-use,
 * expiring code; this page is where it is redeemed. There is no "request an
 * invite" and no way to sign up without one, and this page does not pretend
 * otherwise.
 *
 * The checks here (username shape, password length) are a convenience that
 * saves a round trip; the server re-checks every one and is the authority. They
 * are written from the same constants as the hints under the fields
 * (`i18n/auth-labels.ts`), so the hint and the check cannot disagree.
 *
 * Password length is counted in code points, like the server does, so a
 * password of twelve emoji is accepted by both and not by neither.
 *
 * The invite link carries its code (`/signup?code=...`, #434): it only seeds the
 * field, which stays visible and editable, and is trimmed like typed input.
 *
 * The link opens on a welcome step (#655), not on the form: a short brief and Next at the bottom, then the account
 * form on the same route. ~~The brief sat inside the form as three lines, one of them "Run by Ben".~~ *Amended
 * 2026-11-04 (#655):* the brief is its own first step and says nothing about data and nothing about who runs the pilot;
 * anything about data is consent text (UX_EXPERIENCE §4.2). The welcome makes no API request. Next moves focus to the
 * account step's heading; focus scrolls it into view, so nothing calls `window.scrollTo`.
 */

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router';
import type { SignupRequest } from '@davinci/contracts';
import { ApiRequestError } from '../../api/client.js';
import { useApi } from '../../app/api-context.js';
import { AuthHeroDots, AuthStage, SignupSteps } from '../../app/auth-layout.js';
import { FailureNotice } from '../../app/failure-notice.js';
import {
  authMessages,
  MAX_PASSWORD_LENGTH,
  MIN_PASSWORD_LENGTH,
  signUpFailureSentence,
  USERNAME_PATTERN,
} from '../../i18n/auth-labels.js';
import { useMessages } from '../../i18n/locale.js';
import { defineMessages } from '../../i18n/messages.js';

const messages = defineMessages({
  en: {
    welcomeTitle: 'Welcome to DaVinci',
    welcomeLead: "Post your maths homework. Get feedback before it's due.",
    welcome1: 'You solve it, DaVinci checks it',
    welcome2: 'Feedback on your own pages',
    welcome3: 'See your progress, topic by topic',
    next: 'Next',
    accountTitle: 'Create your account',
    inviteHint: 'Use the invite code you were given. It works once and expires.',
    fromLink: 'from your link',
    pilot: 'A research pilot',
    errorHeading: 'DaVinci could not create your account',
    inviteCodeProblemLabel: 'Invite code:',
    usernameProblemLabel: 'Username:',
    passwordProblemLabel: 'Password:',
    inviteCodeProblem: 'Enter the invite code you were given.',
    inviteCode: 'Invite code',
    username: 'Username',
    displayName: 'Display name',
    displayNameHint: 'Optional. How the app addresses you. You can change it later.',
    password: 'Password',
    submitting: 'Creating your account…',
    submit: 'Create account',
    signIn: 'Sign in',
  },
  he: {
    welcomeTitle: 'ברוכים הבאים ל-DaVinci',
    welcomeLead: 'מעלים את שיעורי הבית במתמטיקה ומקבלים משוב לפני ההגשה.',
    welcome1: 'הפתרון שלך, הבדיקה עלינו',
    welcome2: 'משוב על הדפים שלך',
    welcome3: 'רואים את ההתקדמות לפי נושא',
    next: 'הבא',
    accountTitle: 'פתיחת חשבון',
    inviteHint: 'קוד ההזמנה מופיע בהזמנה. אפשר להשתמש בו פעם אחת והוא פג תוקף.',
    fromLink: 'מהקישור',
    pilot: 'פיילוט מחקרי',
    errorHeading: 'DaVinci לא הצליח ליצור את החשבון שלך',
    inviteCodeProblemLabel: 'קוד הזמנה:',
    usernameProblemLabel: 'שם משתמש:',
    passwordProblemLabel: 'סיסמה:',
    inviteCodeProblem: 'יש להזין את קוד ההזמנה שקיבלת.',
    inviteCode: 'קוד הזמנה',
    username: 'שם משתמש',
    displayName: 'שם לתצוגה',
    displayNameHint: 'לא חובה. איך האפליקציה פונה אליך. אפשר לשנות מאוחר יותר.',
    password: 'סיסמה',
    submitting: 'יוצר את החשבון שלך…',
    submit: 'יצירת חשבון',
    signIn: 'כניסה',
  },
});

interface FieldProblems {
  readonly inviteCode?: true;
  readonly username?: true;
  readonly password?: true;
}

function checkFields(fields: { inviteCode: string; username: string; password: string }): FieldProblems {
  const problems: { inviteCode?: true; username?: true; password?: true } = {};
  if (fields.inviteCode === '') problems.inviteCode = true;
  if (!USERNAME_PATTERN.test(fields.username)) problems.username = true;
  const length = [...fields.password.normalize('NFC')].length;
  if (length < MIN_PASSWORD_LENGTH || length > MAX_PASSWORD_LENGTH) problems.password = true;
  return problems;
}

export function SignupPage(): React.JSX.Element {
  const t = useMessages(messages);
  const a = useMessages(authMessages);
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const codeFromLink = (searchParams.get('code') ?? '').trim();
  const [inviteCode, setInviteCode] = useState(() => codeFromLink);
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [problems, setProblems] = useState<FieldProblems>({});
  const summaryRef = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState<'welcome' | 'account'>('welcome');
  const accountHeadingRef = useRef<HTMLHeadingElement>(null);

  const mutation = useMutation({
    mutationFn: (body: SignupRequest) => api.signup(body),
    onSuccess: () => {
      queryClient.clear();
      void navigate('/homework', { replace: true });
    },
    onError: () => setPassword(''),
  });

  const serverSentence =
    mutation.error instanceof ApiRequestError ? signUpFailureSentence(mutation.error.code, a) : undefined;
  const hasFieldProblems = Object.keys(problems).length > 0;
  const showSummary = hasFieldProblems || serverSentence !== undefined;

  useEffect(() => {
    if (showSummary) summaryRef.current?.focus();
  }, [showSummary, mutation.submittedAt]);

  // Focus follows Next to the new heading; it does not run on first mount (the welcome step has the page's focus).
  useEffect(() => {
    if (step === 'account') accountHeadingRef.current?.focus();
  }, [step]);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const fields = { inviteCode: inviteCode.trim(), username: username.trim(), password };
    const found = checkFields(fields);
    setProblems(found);
    if (Object.keys(found).length > 0) return;

    const trimmedName = displayName.trim();
    mutation.mutate({
      inviteCode: fields.inviteCode,
      username: fields.username,
      password: fields.password,
      ...(trimmedName === '' ? {} : { displayName: trimmedName }),
    });
  }

  const usernameTaken = mutation.error instanceof ApiRequestError && mutation.error.code === 'username_taken';

  if (step === 'welcome') {
    return (
      <AuthStage glyph={<AuthHeroDots />} heroOnPhone>
        <h1>{t.welcomeTitle}</h1>
        <p className="auth-lead">{t.welcomeLead}</p>
        <ul className="auth-facts">
          <li data-course="teal">
            <span className="auth-fact-mark" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 20l1-4L16 5l3 3L8 19z" />
              </svg>
            </span>
            {t.welcome1}
          </li>
          <li data-course="ochre">
            <span className="auth-fact-mark" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 3h7l4 4v14H7z" />
                <path d="M10 12h5M10 16h5" />
              </svg>
            </span>
            {t.welcome2}
          </li>
          <li data-course="plum">
            <span className="auth-fact-mark" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 18h5v-5h5V8h6" />
              </svg>
            </span>
            {t.welcome3}
          </li>
        </ul>
        <button className="auth-submit" type="button" onClick={() => setStep('account')}>
          {t.next}
        </button>
        <p className="auth-foot">
          {t.pilot} {'·'} <Link to="/login">{t.signIn}</Link>
        </p>
      </AuthStage>
    );
  }

  return (
    <AuthStage glyph={'✉'}>
      <SignupSteps current="account" />
      <h1 tabIndex={-1} ref={accountHeadingRef}>
        {t.accountTitle}
      </h1>

      {showSummary ? (
        <div className="notice notice-error" role="alert" tabIndex={-1} ref={summaryRef}>
          <h2>{t.errorHeading}</h2>
          {hasFieldProblems ? (
            <ul>
              {problems.inviteCode !== undefined ? (
                <li>
                  <strong>{t.inviteCodeProblemLabel}</strong> {t.inviteCodeProblem}
                </li>
              ) : null}
              {problems.username !== undefined ? (
                <li>
                  <strong>{t.usernameProblemLabel}</strong> {a.usernameRule}
                </li>
              ) : null}
              {problems.password !== undefined ? (
                <li>
                  <strong>{t.passwordProblemLabel}</strong> {a.passwordRule}
                </li>
              ) : null}
            </ul>
          ) : (
            <p>{serverSentence}</p>
          )}
        </div>
      ) : mutation.isError ? (
        <FailureNotice error={mutation.error} />
      ) : null}

      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        <div className="field">
          <label htmlFor="signup-invite-code">{t.inviteCode}</label>
          <div className="auth-code">
            <input
              id="signup-invite-code"
              name="inviteCode"
              type="text"
              dir="ltr"
              required
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              value={inviteCode}
              aria-invalid={problems.inviteCode !== undefined}
              aria-describedby={codeFromLink === '' ? 'signup-invite-hint' : undefined}
              onChange={(event) => setInviteCode(event.target.value)}
            />
            {codeFromLink !== '' && inviteCode.trim() === codeFromLink ? (
              <span className="auth-chip">
                <span aria-hidden="true">{'✓'}</span>
                {t.fromLink}
              </span>
            ) : null}
          </div>
          {codeFromLink === '' ? (
            <p className="field-hint" id="signup-invite-hint">
              {t.inviteHint}
            </p>
          ) : null}
        </div>

        <div className="field">
          <label htmlFor="signup-username">{t.username}</label>
          <p className="field-hint" id="signup-username-hint">
            {a.usernameRule}
          </p>
          <input
            id="signup-username"
            name="username"
            type="text"
            required
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            dir="ltr"
            maxLength={32}
            value={username}
            aria-describedby="signup-username-hint"
            aria-invalid={problems.username !== undefined || usernameTaken}
            onChange={(event) => setUsername(event.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="signup-display-name">{t.displayName}</label>
          <p className="field-hint" id="signup-display-name-hint">
            {t.displayNameHint}
          </p>
          <input
            id="signup-display-name"
            name="displayName"
            type="text"
            maxLength={64}
            autoComplete="name"
            value={displayName}
            aria-describedby="signup-display-name-hint"
            onChange={(event) => setDisplayName(event.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="signup-password">{t.password}</label>
          <p className="field-hint" id="signup-password-hint">
            {a.passwordRule}
          </p>
          <input
            id="signup-password"
            name="password"
            type="password"
            dir="ltr"
            required
            autoComplete="new-password"
            value={password}
            aria-describedby="signup-password-hint"
            aria-invalid={problems.password !== undefined}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>

        <button className="auth-submit" type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? t.submitting : t.submit}
        </button>
      </form>

      <p className="auth-foot">
        {t.pilot} {'·'} <Link to="/login">{t.signIn}</Link>
      </p>
    </AuthStage>
  );
}
